import { IUserExperience, UserExperienceState, UserExperienceType, UserExperienceSubtype } from "common/interfaces";
import { userExperiences as userExperiencesApi } from "apis-v2/callables";
import { sessionId, browserId } from "js/core/browserAndSession";
import { auth } from "js/firebase/auth";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { getSequentialRunner } from "common/utils/runSequentially";
import { releaseVersion, buildVersion, clientName, isCypress } from "js/config";

const logger = getLogger(LogGroup.USER_EXPERIENCES);

const runSequentially = getSequentialRunner();

let currentExperience: IUserExperience | null = null;

function enrichMetadata(metadata?: Partial<IUserExperience["metadata"]>) {
    return {
        ...(metadata ?? {}),
        sessionId,
        browserId,
        uid: auth().currentUser?.uid,
        releaseVersion,
        buildVersion,
        clientName,
        workspaceId: metadata?.workspaceId ?? AppController.workspaceId,
    } as IUserExperience["metadata"];
}

function updateCurrentExperience({
    type,
    state,
    metadata,
    condition,
    reregister
}: {
    type: UserExperienceType | UserExperienceType[];
    state?: UserExperienceState;
    metadata: Partial<IUserExperience["metadata"]>;
    condition?: (metadata: IUserExperience["metadata"]) => boolean;
    reregister?: boolean;
}) {
    runSequentially(async () => {
        if (reregister && Array.isArray(type)) {
            throw new Error("Cannot reregister for multiple user experience types");
        }

        if (!currentExperience) {
            return;
        }

        if (currentExperience.state !== UserExperienceState.STARTED) {
            return;
        }

        if (Array.isArray(type) ? !type.includes(currentExperience.type) : currentExperience.type !== type) {
            return;
        }

        if (condition && !condition(currentExperience.metadata)) {
            return;
        }

        if (metadata?.error && metadata.error instanceof Error) {
            metadata.error = [...Object.getOwnPropertyNames(metadata.error), "name"].reduce((err, key) => ({
                ...err,
                [key]: metadata.error[key]
            }), {});
        }

        try {
            currentExperience = await userExperiencesApi.update({
                id: currentExperience.id,
                metadata: enrichMetadata(metadata),
                ...(state ? { state } : {})
            });

            if (reregister && [UserExperienceState.FAILED, UserExperienceState.COMPLETED, UserExperienceState.ABANDONED].includes(state)) {
                currentExperience = await userExperiencesApi.register({
                    type: type as UserExperienceType,
                    metadata: enrichMetadata({
                        ...currentExperience.metadata,
                        ...metadata,
                        error: undefined,
                        subtype: undefined
                    })
                });
            }
        } catch (err) {
            logger.error(err, "updateCurrentExperience() failed to update current user experience", { type, state });
        }
    });
}

/**
 * Registers a new user experience, if there is a started current experience of the same type, it will be kept and updated with the new metadata.
 */
function register(type: UserExperienceType, metadata?: Partial<IUserExperience["metadata"]>) {
    if (isCypress) {
        logger.info("register() skipping for Cypress", { type, metadata });
        return;
    }

    runSequentially(async () => {
        try {
            if (currentExperience && currentExperience.type === type && currentExperience.state === UserExperienceState.STARTED) {
                currentExperience = await userExperiencesApi.update({
                    id: currentExperience.id,
                    metadata: enrichMetadata(metadata)
                });

                return;
            }

            currentExperience = await userExperiencesApi.register({
                type,
                metadata: enrichMetadata(metadata)
            });
        } catch (err) {
            logger.error(err, "register() failed to register user experience", { type });

            currentExperience = null;
        }
    });
}

/**
 * Completes the current user experience if it has the same type and is started.
 */
function complete(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.COMPLETED,
        metadata,
        condition,
        reregister
    });
}

/**
 * Abandons the current user experience if it has the same type and is started.
 */
function abandon(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.ABANDONED,
        metadata,
        condition,
        reregister
    });
}

/**
 * Fails the current user experience if it has the same type and is started.
 */
function fail(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.FAILED,
        metadata,
        condition,
        reregister
    });
}

/**
 * Updates the current user experience with new metadata if it has the same type and is started.
 */
function update(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean) {
    updateCurrentExperience({
        type,
        metadata,
        condition
    });
}

function wrap(type: UserExperienceType) {
    return {
        register: (metadata?: Partial<IUserExperience["metadata"]>) => register(type, metadata),
        complete: (metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) => complete(type, metadata, condition, reregister),
        abandon: (metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) => abandon(type, metadata, condition, reregister),
        fail: (metadata: Partial<IUserExperience["metadata"]> & Pick<IUserExperience["metadata"], "error">, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) => fail(type, metadata, condition, reregister),
        update: (metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean) => update(type, metadata, condition)
    };
}

const methods = Object.fromEntries(
    Object.values(UserExperienceType).map(type => [type, wrap(type)])
) as Record<UserExperienceType, ReturnType<typeof wrap>>;

export default {
    ...methods,
    Type: UserExperienceType,
    State: UserExperienceState,
    Subtype: UserExperienceSubtype
};
